"use client";

import clsx, {ClassValue} from "clsx";
import React from "react";
import Tooltip from "./Tooltip";
import Icon, {IconNameExtended, ICON_SIZES} from "./Icon";

const DEFAULT_BRAND = "default";

export type ButtonLinkProps = {
   href: string;
   target?: React.HTMLAttributeAnchorTarget;
   rel?: string;
   prefetch?: boolean;
   className?: string;
   id?: string;
   onClick?: () => void;
   children?: React.ReactNode;
   "data-testid"?: string;
   disabled?: boolean;
   "aria-label"?: string;
   title?: string;
};

export type BaseButtonType =
   | "primary"
   | "secondary"
   | "tertiary"
   | "none"
   | "link";

type NativeButtonProps = Omit<
   React.ButtonHTMLAttributes<HTMLButtonElement>,
   "type" | "onClick" | "children" | "className"
>;

export type BaseButtonProps = NativeButtonProps & {
   children?: React.ReactNode;
   label?: React.ReactNode;
   /** Visual variant (not the HTML `type` attribute — use `buttonType` for that). */
   type?: BaseButtonType;
   buttonType?: "button" | "submit" | "reset";
   compact?: boolean;
   wide?: boolean;
   disabled?: boolean;
   restrictedText?: string;
   id?: string;
   href?: string;
   target?: "_blank" | "_self" | "_parent" | "_top";
   onClick?: React.MouseEventHandler<HTMLButtonElement>;
   secondaryAction?: () => void;
   iconBefore?: React.ReactNode | string;
   iconAfter?: React.ReactNode | string;
   testId?: string;
   className?: ClassValue;
   prefetch?: boolean;
   iconOnly?: boolean;
   destructive?: boolean;
   /** Org brand key. Web passes this from useOrg; "default" uses Gail primary styles. */
   brand?: string;
   /** When set, development-only prop checks run. Web passes NEXT_PUBLIC_ENVIRONMENT === "development". */
   devChecks?: boolean;
   /** Link renderer. Defaults to a plain anchor so this package does not import next/link. */
   linkComponent?: React.ComponentType<ButtonLinkProps & React.RefAttributes<HTMLAnchorElement>>;
};

const DefaultLink = React.forwardRef<HTMLAnchorElement, ButtonLinkProps>(
   function DefaultLink({ prefetch: _prefetch, ...props }, ref) {
      return <a ref={ref} {...props} />;
   }
);

const BaseButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, BaseButtonProps>(
   function BaseButton(
      {
         href,
         onClick,
         type = "primary",
         buttonType = "button",
         className,
         id,
         testId: dataTestId,
         wide,
         compact,
         disabled,
         restrictedText,
         children,
         iconBefore: iconBeforeProp,
         iconAfter: iconAfterProp,
         prefetch = true,
         target = "_self",
         secondaryAction,
         iconOnly = false,
         destructive = false,
         title,
         label,
         brand = DEFAULT_BRAND,
         devChecks = process.env.NODE_ENV === "development",
         linkComponent: LinkComponent,
         "aria-label": ariaLabel,
         ...rest
      },
      ref
   ) {
      let iconBefore = iconBeforeProp;
      let iconAfter = iconAfterProp;
      const LinkView = LinkComponent ?? DefaultLink;

      if (
         href === undefined &&
         !onClick &&
         buttonType === "button" &&
         devChecks
      ) {
         throw new Error(
            "You must have an href or onClick prop when buttonType is 'button'"
         );
      }

      if (typeof iconBefore === "string") {
         iconBefore = <Icon icon={iconBefore as IconNameExtended} />;
      }
      if (typeof iconAfter === "string") {
         iconAfter = <Icon icon={iconAfter as IconNameExtended} />;
      }

      if (href !== undefined && target === "_blank" && !iconAfter) {
         iconAfter = (
            <Icon
               icon="arrow-up-right-from-square"
               size={compact ? ICON_SIZES.XXS : ICON_SIZES.SM}
            />
         );
      }

      let resolvedDisabled = disabled;
      if (restrictedText) {
         resolvedDisabled = true;
      }

      const resolvedAriaLabel =
         ariaLabel ?? (iconOnly && typeof label === "string" ? label : undefined);

      if (
         iconOnly &&
         !resolvedAriaLabel &&
         devChecks
      ) {
         console.warn(
            "BaseButton: iconOnly buttons must include aria-label or a string label prop."
         );
      }

      const commonProps = {
         id,
         disabled: resolvedDisabled,
         title,
         "data-testid": dataTestId,
         className: clsx(
            {
               ["rounded-full"]: true,
               ["py-2 px-6"]: !compact,
               ["py-1 px-3"]: compact,
               ["bg-gailPrimary hover:bg-gailPrimary/65 text-white"]:
                  !resolvedDisabled && type === "primary",
               ["bg-gailPrimary/50 text-white/50"]: resolvedDisabled && type === "primary",
               ["bg-transparent border border-gailPrimary text-gailPrimary hover:bg-gailPrimary/15 dark:border-white dark:text-white dark:hover:bg-white/15"]:
                  !resolvedDisabled && type === "secondary" && brand === DEFAULT_BRAND,
               ["bg-transparent border border-gailPrimary/50 text-gailPrimary/50 dark:border-white/50 dark:text-white/50"]:
                  resolvedDisabled && type === "secondary" && brand === DEFAULT_BRAND,
               ["bg-button_secondary border border-button_secondary text-button_text_secondary hover:bg-button_secondary/50"]:
                  !resolvedDisabled && type === "secondary" && brand !== DEFAULT_BRAND,
               ["bg-button_secondary border border-button_secondary/50 text-button_text_secondary/50"]:
                  resolvedDisabled && type === "secondary" && brand !== DEFAULT_BRAND,
               ["bg-transparent text-700 border border-neutral hover:bg-700/15 dark:hover:bg-gray-700/85"]:
                  !resolvedDisabled && type === "tertiary",
               ["bg-700/10 text-500 border border-neutral"]: resolvedDisabled && type === "tertiary",
               ["cursor-not-allowed"]: resolvedDisabled,
               ["w-full"]: wide,
               ["flex"]: !!href,
               ["inline-block"]: !!onClick,
               ["!p-0 !m-0 text-black dark:text-white"]: type === "link",
               ["hover:bg-transparent hover:text-primary"]: type === "link",
               ["!px-2 !rounded-full !m-0 !border-0 !border-transparent"]: iconOnly,
               ["bg-surface border !border-red-500 hover:bg-red-500/10 !text-red-500"]: destructive,
            },
            className
         ),
      };

      const buttonChildren = (
         <div
            className={clsx("m-auto text-center font-monument whitespace-nowrap", {
               ["text-sm"]: compact,
               ["flex items-center gap-2 justify-center"]: iconBefore || iconAfter,
            })}
         >
            {iconBefore && (
               <span className="inline-flex items-center justify-center leading-none text-current">
                  {iconBefore}
               </span>
            )}
            {children}
            {iconAfter && (
               <span className="inline-flex items-center justify-center leading-none text-current">
                  {iconAfter}
               </span>
            )}
         </div>
      );

      if (onClick || buttonType !== "button") {
         const buttonComponent = (
            <button
               ref={ref as React.Ref<HTMLButtonElement>}
               {...rest}
               type={buttonType}
               onClick={onClick}
               aria-label={resolvedAriaLabel}
               {...commonProps}
            >
               {buttonChildren}
            </button>
         );

         if (restrictedText) {
            return (
               <div className={clsx({ "[&>div:first-of-type]:w-full": wide })}>
                  <Tooltip content={restrictedText}>{buttonComponent}</Tooltip>
               </div>
            );
         }

         return buttonComponent;
      }

      if (href !== undefined) {
         const linkComponent = (
            <LinkView
               ref={ref as React.Ref<HTMLAnchorElement>}
               href={href}
               target={target}
               rel={target === "_blank" ? "noopener noreferrer" : undefined}
               {...commonProps}
               prefetch={prefetch}
               onClick={secondaryAction ? () => secondaryAction() : undefined}
            >
               {buttonChildren}
            </LinkView>
         );

         if (restrictedText) {
            return (
               <div className={clsx({ "[&>div:first-of-type]:w-full": wide })}>
                  <Tooltip content={restrictedText}>{linkComponent}</Tooltip>
               </div>
            );
         }

         return linkComponent;
      }

      return null;
   }
);

BaseButton.displayName = "BaseButton";

export default BaseButton;
